import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { LinkSimple, Storefront, Sparkle, Trophy, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * <SuggestedMatches kind="wishlist" id={...} source={...} onLinked={refresh} />
 * Auto-fetches cached cross-retailer suggestions (computed in the background on creation)
 * and presents them as one-click "Track this too" cards. Re-fetch on demand.
 */
export default function SuggestedMatches({ kind, id, source, onLinked }) {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [linking, setLinking] = useState(null);

  const sourceRetailer = source?.retailer_id;
  const linkedRids = new Set((source?.linked_listings || []).map((l) => l.retailer_id));

  const load = async (refresh = false) => {
    if (kind !== "wishlist") return;
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/${kind}/${id}/suggested-matches${refresh ? "?refresh=true" : ""}`);
      setMatches(data.matches || []);
    } catch {
      setMatches([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [id]);

  const accept = async (m) => {
    setLinking(m.url);
    try {
      await api.post(`/${kind}/${id}/link-listing`, { url: m.url });
      toast.success(`Now tracking at ${m.retailer_name}`);
      setMatches((prev) => (prev || []).filter((x) => x.url !== m.url));
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start tracking that retailer.");
    } finally { setLinking(null); }
  };

  if (kind !== "wishlist") return null;

  // Only surface high-confidence cross-retailer matches. The backend
  // matcher returns anything >= 0.55; we apply a tighter 0.75 floor on
  // the UI side so we never display a wobbly "this might be it" tile.
  // A user complaint about a 100" LG TV being matched to a Fire TV
  // Stick (cached from an older matcher version) anchored this number.
  const HIGH_CONF = 0.75;
  const fresh = (matches || [])
    .filter((m) => !linkedRids.has(m.retailer_id) && m.retailer_id !== sourceRetailer)
    .filter((m) => (m.confidence || 0) >= HIGH_CONF);

  return (
    <div className="border border-[#e4e4e7] bg-white" data-testid="suggested-matches-section">
      <div className="bg-[#0a0a0a] text-white px-6 py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Sparkle size={20} weight="fill" color="#fff" />
          <div>
            <div className="font-heading font-bold tracking-tight">
              {loading ? "Searching other retailers…" : fresh.length > 0 ? `${fresh.length} other retailer${fresh.length === 1 ? "" : "s"} stock this` : "No other retailers matched yet"}
            </div>
            <div className="text-xs text-white/60 mt-0.5 font-mono-pg uppercase tracking-widest">
              Pick the ones you want us to monitor for price drops
            </div>
          </div>
        </div>
        <button onClick={() => load(true)} disabled={refreshing || loading} className="text-xs font-mono-pg uppercase tracking-widest text-white/70 hover:text-white inline-flex items-center gap-1.5" data-testid="suggested-matches-refresh">
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} /> Re-scan
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="space-y-3" data-testid="suggested-matches-loading">
            {[1, 2].map((i) => <div key={i} className="h-20 bg-[#f0f0f2] animate-pulse" />)}
          </div>
        ) : fresh.length === 0 ? (
          <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-6 text-center text-sm text-[#52525b]" data-testid="suggested-matches-empty">
            We couldn't auto-find this product at the other retailers we cover yet. If you spot it somewhere else,
            <details className="inline-block ml-1 align-baseline">
              <summary className="cursor-pointer underline">paste the URL</summary>
              <ManualLink kind={kind} id={id} onLinked={onLinked} />
            </details>
            and we'll start tracking it daily.
          </div>
        ) : (
          <ul className="space-y-3">
            {fresh.map((m) => (
              <li key={m.url} className="border border-[#e4e4e7] bg-[#f7f7f8] p-4 flex items-center gap-4" data-testid={`suggested-match-${m.retailer_id}`}>
                {m.image && <img src={m.image} alt="" referrerPolicy="no-referrer" onError={(e)=>{ e.currentTarget.style.display="none"; }} className="w-14 h-14 object-cover border border-[#e4e4e7] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Storefront size={14} className="text-[#52525b]" />
                    <span className="pg-overline">{m.retailer_name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: m.confidence >= 0.8 ? "#16A34A" : m.confidence >= 0.5 ? "#002FA7" : "#D97706" }}>
                      {Math.round(m.confidence * 100)}% match
                    </span>
                  </div>
                  <a href={m.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:underline truncate block mt-1">{m.name}</a>
                  <div className="font-mono-pg text-xs text-[#52525b] mt-1">
                    ${Number(m.price).toFixed(2)} NZD · {m.in_stock ? "In stock" : "Out of stock"}
                  </div>
                </div>
                <button onClick={() => accept(m)} disabled={linking === m.url} className="bg-[#002FA7] text-white font-bold px-4 py-2 text-sm hover:bg-[#00207A] disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0" data-testid={`accept-match-${m.retailer_id}`}>
                  {linking === m.url ? "Adding…" : <><LinkSimple size={14} weight="bold" /> Track this too</>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ManualLink({ kind, id, onLinked }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!url) return;
    setBusy(true);
    try {
      await api.post(`/${kind}/${id}/link-listing`, { url });
      toast.success("Linked");
      setUrl("");
      onLinked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 flex gap-2 max-w-lg">
      <input type="url" value={url} onChange={(e)=>setUrl(e.target.value)} placeholder="https://www.pbtech.co.nz/product/…" className="flex-1 border border-[#e4e4e7] py-2 px-3 outline-none focus:border-[#002FA7] text-sm" data-testid="suggested-manual-input" />
      <button type="submit" disabled={busy || !url} className="bg-[#002FA7] text-white font-bold px-4 py-2 text-sm hover:bg-[#00207A] disabled:opacity-60" data-testid="suggested-manual-submit">
        {busy ? "Linking…" : "Link"}
      </button>
    </form>
  );
}
